import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {TransitionGroup} from 'react-transition-group'
import AngleDown from '../../../assets/icons/AngleDown.svg'
import FullStar from '../../../assets/icons/FullStar.svg'
import defaultProfile from '../../../assets/imgs/profile-default.png'
import {Locale, OrderType, UserType} from '../../../config/constants'
import {dateOnlyOption, FormattedDateClient} from '../../../helpers/dateUtil'
import {getFullName} from '../../../helpers/util'
import ReviewPhotoList from '../../carOrStation/review/ReviewPhotoList'
import FadeTransition from '../../common/FadeTransition'
import ResponsiveImg from '../../common/ResponsiveImg'
import {useUserType, useLocale} from '../../hocs/hooks'
import {IHolderReview} from './CarReview'
import styles from './CarReviewPanel.scss'

export interface IReview {
  userReviewContent: string,
  userReviewStars: number,
  user?: {
    name: string,
    picture: string,
  },
  userReviewCreatedAt: string | Date,
  reviewPhotos: ReadonlyArray<{ url: string, caption?: string }>
}


export const CarStar = ({stars}: {stars: number}) => <>
  <FullStar className={['fa mr-1 pb-1', styles.starColor].join(' ')}/>
  <FormattedNumber value={stars} minimumFractionDigits={2} maximumFractionDigits={2}/>
</>

const maxContentHeight = 90 //px
const CarReviewPanel = ({review}: {review: IReview | IHolderReview}) => {
  const userType = useUserType()
  const locale = useLocale()
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(true)
  const [bigContent, setBigContent] = useState(false)
  useEffect(() => {
    const isLong = ref.current!.clientHeight >= maxContentHeight
    if (bigContent !== isLong) {
      setOpen(!isLong)
      setBigContent(isLong)
    }
  }, [bigContent])
  const toggleOpen = () => setOpen(!open)
  const {userReviewContent, holderReviewContent, userReviewStars, holderReviewStars,
        user, holder, userReviewCreatedAt, holderReviewCreatedAt, reviewPhotos, holderReviewPhotos} = review
  const isUser = userType === UserType.user
  return <div>
    <div className="row ml-0">
      <ResponsiveImg
        width={50}
        height={50}
        className={[styles.profilePhoto, 'rounded-circle mt-1'].join(' ')}
        url={(isUser ? user?.picture : holder?.picture) || defaultProfile}
        caption="User image"/>
      <div className="ml-2">
        <div className="font-weight-bold">
          {(isUser ? getFullName(locale, user) : getFullName(Locale.ja, holder)) || <FormattedMessage id="user.deletedAccount" />}
        </div>
        <div>
          <CarStar stars={isUser ? userReviewStars : holderReviewStars}/>
          <span className="ml-2"><FormattedDateClient value={isUser ? userReviewCreatedAt : holderReviewCreatedAt} {...dateOnlyOption}/></span>
        </div>
      </div>
    </div>
    <ReviewPhotoList photos={isUser ? reviewPhotos : holderReviewPhotos} orderType={OrderType.car}/>
    <div className="position-relative mt-2">
      <p className={[!open && styles.contentClose, 'pre-wrap mb-0'].filter(Boolean).join(' ')}
           ref={ref}>
        {isUser ? userReviewContent : holderReviewContent}
      </p>
      <TransitionGroup component={null}>
        {bigContent && !open && <FadeTransition>
          <div className={styles.contentFade}/>
        </FadeTransition>
        }
      </TransitionGroup>
    </div>
    {bigContent && <div>
      <AngleDown className={['fa font-size-75 mr-2', styles.icon, open && styles.rotate].filter(Boolean).join(' ')}/>
      <div className="text-primary c-pointer d-inline-block text-van" onClick={toggleOpen}>
        {open
          ? <FormattedMessage id="review.showLess"/>
          : <FormattedMessage id="review.showMore"/>
        }
      </div>
    </div>}
  </div>
}
CarReviewPanel.displayName = 'CarReviewPanel'
export default CarReviewPanel
